import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, Save, FileSpreadsheet, Wrench, Settings as SettingsIcon, Mail, Send, MessageCircle, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { SoftwareLicensesCard } from "@/components/SoftwareLicensesCard";
import { GenericWarrantyQrCard } from "@/components/GenericWarrantyQrCard";
import { PriceHistoryCard } from "@/components/PriceHistoryCard";
import { EmailLogCard } from "@/components/EmailLogCard";

function SmtpCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/settings/smtp");
      setCfg(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // Only send secrets if user edited them (not the masked placeholder)
      if (payload.password === "••••••••") delete payload.password;
      if (payload.resend_api_key === "••••••••") delete payload.resend_api_key;
      delete payload.smtp_fallback;
      const { data } = await api.put("/settings/smtp", payload);
      setCfg(data);
      toast.success("Configurazione email salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Inserisci un indirizzo email valido"); return; }
    setTesting(true);
    try {
      await api.post("/settings/smtp/test", { to: testTo });
      toast.success(`Email di prova inviata a ${testTo}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const isResend = cfg.provider === "resend";

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-smtp">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
          <Mail className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Notifiche email</h3>
          <p className="text-sm text-zinc-600">Canale di invio per notifiche automatiche di cambio stato, nuovo ticket, assegnazione, chiusura, preventivi e garanzie.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            data-testid="smtp-enabled"
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            className="h-4 w-4 rounded-sm border-zinc-300 accent-brand"
          />
          <span className="text-xs uppercase tracking-widest text-zinc-600">{cfg.enabled ? "Attive" : "Disattive"}</span>
        </label>
      </div>

      <div className="mb-5">
        <Label className="text-xs uppercase tracking-widest text-zinc-500">Provider di invio</Label>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="email-provider-resend"
            onClick={() => setCfg({ ...cfg, provider: "resend" })}
            className={`text-left rounded-sm border p-3 transition-colors ${isResend ? "border-brand bg-brand-muted/30" : "border-zinc-200 hover:border-zinc-300"}`}
          >
            <div className="text-sm font-semibold text-zinc-900">Resend (API) <span className="ml-1 text-[10px] uppercase tracking-widest text-brand">consigliato</span></div>
            <div className="text-xs text-zinc-600 mt-1">Invio via HTTPS dal dominio verificato: non soggetto ai blocchi IP dei server SMTP (es. Aruba).</div>
          </button>
          <button
            type="button"
            data-testid="email-provider-smtp"
            onClick={() => setCfg({ ...cfg, provider: "smtp" })}
            className={`text-left rounded-sm border p-3 transition-colors ${!isResend ? "border-brand bg-brand-muted/30" : "border-zinc-200 hover:border-zinc-300"}`}
          >
            <div className="text-sm font-semibold text-zinc-900">SMTP</div>
            <div className="text-xs text-zinc-600 mt-1">Server di posta tradizionale (Aruba, Gmail, Microsoft 365…).</div>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Mittente (From)</Label>
          <Input data-testid="smtp-from" value={cfg.sender || ""} onChange={(e) => setCfg({ ...cfg, sender: e.target.value })} placeholder="assistance@wineemotion.com" className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome mittente</Label>
          <Input data-testid="smtp-from-name" value={cfg.sender_name || ""} onChange={(e) => setCfg({ ...cfg, sender_name: e.target.value })} placeholder="Wineemotion Assistenza" className="mt-1 rounded-sm" />
        </div>
        {isResend && (
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Resend API key</Label>
            <Input data-testid="resend-api-key" type="password" value={cfg.resend_api_key || ""} onChange={(e) => setCfg({ ...cfg, resend_api_key: e.target.value })} placeholder="re_…" className="mt-1 rounded-sm" />
            <p className="text-[11px] text-zinc-500 mt-1">Il dominio del mittente deve essere verificato su resend.com. Se Resend non è raggiungibile, l'invio ricade automaticamente sul server SMTP qui sotto (se configurato).</p>
          </div>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-zinc-100">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{isResend ? "Server SMTP (fallback opzionale)" : "Server SMTP"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Server SMTP (host)</Label>
          <Input data-testid="smtp-host" value={cfg.host || ""} onChange={(e) => setCfg({ ...cfg, host: e.target.value })} placeholder="mail.wineemotion.com" className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Porta</Label>
          <Input data-testid="smtp-port" type="number" value={cfg.port || 587} onChange={(e) => setCfg({ ...cfg, port: parseInt(e.target.value, 10) || 587 })} className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Utente</Label>
          <Input data-testid="smtp-user" value={cfg.user || ""} onChange={(e) => setCfg({ ...cfg, user: e.target.value })} placeholder="ticket@wineemotion.com" className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
          <Input data-testid="smtp-password" type="password" value={cfg.password || ""} onChange={(e) => setCfg({ ...cfg, password: e.target.value })} placeholder="••••••••" className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-500">TLS / STARTTLS</Label>
          <div className="mt-2">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="smtp-tls" type="checkbox" checked={!!cfg.use_tls} onChange={(e) => setCfg({ ...cfg, use_tls: e.target.checked })} className="h-4 w-4 rounded-sm accent-brand" />
              Abilita crittografia (raccomandato)
            </label>
          </div>
        </div>
      </div>
      </div>
      <div className="mt-5 pt-4 border-t border-zinc-100 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Invia email di prova a</Label>
          <Input data-testid="smtp-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="tuo.indirizzo@esempio.com" className="mt-1 rounded-sm" />
        </div>
        <Button data-testid="smtp-test-send" onClick={sendTest} disabled={testing} variant="outline" className="rounded-sm">
          {testing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio...</> : <><Send className="h-4 w-4 mr-2" /> Prova invio</>}
        </Button>
        <Button data-testid="smtp-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : <><Save className="h-4 w-4 mr-2" /> Salva email</>}
        </Button>
      </div>
    </Card>
  );
}

function ImportCard({ testid, title, description, endpoint, accept, onDone }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Import completato: ${data.inserted} inseriti, ${data.updated} aggiornati`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
          <FileSpreadsheet className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">{title}</h3>
          <p className="text-sm text-zinc-600 mt-1">{description}</p>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            data-testid={`${testid}-input`}
            onChange={(e) => submit(e.target.files?.[0])}
          />
          <Button
            data-testid={`${testid}-btn`}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="mt-4 bg-brand hover:bg-brand-dark text-white rounded-sm"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importazione...</> : <><Upload className="h-4 w-4 mr-2" /> Carica file</>}
          </Button>
          {result && (
            <div className="mt-4 rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 space-y-1" data-testid={`${testid}-result`}>
              <div><b>{result.inserted}</b> inseriti · <b>{result.updated}</b> aggiornati · {result.skipped} ignorati</div>
              {typeof result.reconciled_tickets === "number" && result.reconciled_tickets > 0 && (
                <div>{result.reconciled_tickets} ticket orfani riconciliati automaticamente</div>
              )}
              {result.errors?.length > 0 && (
                <div className="text-amber-800 mt-1">{result.errors.length} errori (prime {Math.min(3, result.errors.length)}):
                  <ul className="mt-1 list-disc list-inside">
                    {result.errors.slice(0, 3).map((er, i) => <li key={`err-${er.row ?? i}-${(er.error || "").slice(0, 20)}`}>{er.row ? `Riga ${er.row}: ` : ""}{er.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function WhatsAppTemplateStatsCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(null); // null = tutto, or 7 | 30 | 90

  const load = async (nDays = days) => {
    setLoading(true);
    try {
      const params = nDays ? `?days=${nDays}` : "";
      const { data } = await api.get(`/whatsapp/templates/stats${params}`);
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const selectPeriod = (n) => { setDays(n); load(n); };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const periods = [
    { key: 7, label: "7g" },
    { key: 30, label: "30g" },
    { key: 90, label: "90g" },
    { key: null, label: "Tutto" },
  ];

  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-wa-template-stats">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="h-10 w-10 rounded-sm bg-emerald-50 text-emerald-700 flex items-center justify-center">
          <BarChart3 className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Storico template WhatsApp</h3>
          <p className="text-sm text-zinc-600">
            Frequenza di utilizzo e tasso di consegna Meta dei template usati per aprire chat.
            Consegnato = ricevuto sul dispositivo (delivered/read).
          </p>
        </div>
        <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden text-[10px] uppercase tracking-widest">
          {periods.map((p, i) => (
            <button
              key={String(p.key)}
              data-testid={`wa-tpl-period-${p.key ?? "all"}`}
              type="button"
              onClick={() => selectPeriod(p.key)}
              className={`px-3 py-1.5 transition-colors ${i > 0 ? "border-l border-zinc-200" : ""} ${
                days === p.key ? "bg-brand text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-sm"
          onClick={() => load()}
          disabled={loading}
          data-testid="wa-template-stats-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Aggiorna"}
        </Button>
      </div>
      {(!stats || stats.length === 0) ? (
        <div className="text-sm text-zinc-500 py-6 text-center border border-dashed border-zinc-200 rounded-sm" data-testid="wa-template-stats-empty">
          {days ? `Nessun template usato negli ultimi ${days} giorni.` : "Nessun template ancora usato. Apri una chat cliente dal ticket per generare la prima statistica."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                <th className="text-left px-3 py-2 font-medium">Template</th>
                <th className="text-right px-3 py-2 font-medium">Totali</th>
                <th className="text-right px-3 py-2 font-medium">Consegnati</th>
                <th className="text-right px-3 py-2 font-medium">Falliti</th>
                <th className="text-right px-3 py-2 font-medium">Tasso consegna</th>
                <th className="text-left px-3 py-2 font-medium">Ultimo invio</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => {
                const rate = s.success_rate;
                const rateCls = rate >= 80 ? "text-emerald-700" : rate >= 50 ? "text-amber-800" : "text-red-700";
                return (
                  <tr key={s.template} className="border-b border-zinc-100" data-testid={`wa-tpl-row-${s.template}`}>
                    <td className="px-3 py-2 font-mono text-zinc-900">{s.template}</td>
                    <td className="px-3 py-2 text-right text-zinc-800">{s.total}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{s.delivered}</td>
                    <td className="px-3 py-2 text-right text-red-700">{s.failed}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${rateCls}`}>{rate}%</td>
                    <td className="px-3 py-2 text-zinc-600 text-xs">{fmtDate(s.last_sent_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function Impostazioni() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        labor_rate: parseFloat(settings.labor_rate) || 0,
        vat_pct: parseFloat(settings.vat_pct) || 0,
        currency: settings.currency || "EUR",
        company_name: settings.company_name || "Wineemotion",
        company_address: settings.company_address || null,
        company_vat: settings.company_vat || null,
        distributor_hourly_rate: parseFloat(settings.distributor_hourly_rate) || 0,
        distributor_carnet_10h_price: parseFloat(settings.distributor_carnet_10h_price) || 0,
        end_customer_hourly_rate: parseFloat(settings.end_customer_hourly_rate) || 0,
        end_customer_carnet_10h_price: parseFloat(settings.end_customer_carnet_10h_price) || 0,
        accounting_email: (settings.accounting_email || "").trim() || null,
        whatsapp_open_template: (settings.whatsapp_open_template || "").trim() || null,
        whatsapp_open_template_lang: (settings.whatsapp_open_template_lang || "").trim() || null,
      });
      setSettings(data);
      setHistoryKey((k) => k + 1);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="p-8 text-sm text-zinc-500">Caricamento...</div>;

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        title="Impostazioni"
        description="Import di dispenser e ricambi da file, tariffe preventivo e dati azienda."
      />

      <div className="space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Importazione dati</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImportCard
              testid="import-dispensers"
              title="Dispenser (matricole)"
              description="File Excel con colonne DATA PRODUZIONE, ORDINE, SERIAL, MODELLO, Distributore/cliente."
              endpoint="/import/dispensers"
              accept=".xlsx"
            />
            <ImportCard
              testid="import-spare-parts"
              title="Catalogo ricambi"
              description="CSV o Excel con colonne ARCODART (codice), ARDESART (descrizione), LIPREZZO (prezzo)."
              endpoint="/import/spare-parts"
              accept=".csv,.xlsx"
            />
          </div>
        </div>

        <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-quote">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
              <Wrench className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Preventivi</h3>
              <p className="text-sm text-zinc-600">Tariffe di default utilizzate nei preventivi.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tariffa oraria (€/h)</Label>
              <Input
                data-testid="settings-labor-rate"
                type="number" step="0.01" min="0"
                value={settings.labor_rate}
                onChange={(e) => setSettings({ ...settings, labor_rate: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">IVA (%)</Label>
              <Input
                data-testid="settings-vat"
                type="number" step="0.01" min="0"
                value={settings.vat_pct}
                onChange={(e) => setSettings({ ...settings, vat_pct: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Valuta</Label>
              <Input
                data-testid="settings-currency"
                value={settings.currency}
                onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
          </div>
        </Card>

        <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-pricing-hours">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
              <Wrench className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Tariffe assistenza</h3>
              <p className="text-sm text-zinc-600">Prezzi per acquisto ore da parte di distributori e clienti finali (via WhatsApp).</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-zinc-200 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-3 font-medium">Distributore</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">1 ora singola (€)</Label>
                  <Input
                    data-testid="settings-distributor-hourly"
                    type="number" step="0.01" min="0"
                    value={settings.distributor_hourly_rate ?? ""}
                    onChange={(e) => setSettings({ ...settings, distributor_hourly_rate: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Carnet 10 ore (€)</Label>
                  <Input
                    data-testid="settings-distributor-carnet"
                    type="number" step="0.01" min="0"
                    value={settings.distributor_carnet_10h_price ?? ""}
                    onChange={(e) => setSettings({ ...settings, distributor_carnet_10h_price: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
            </div>
            <div className="border border-zinc-200 rounded-sm p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand mb-3 font-medium">Cliente finale (WhatsApp)</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">1 ora singola (€)</Label>
                  <Input
                    data-testid="settings-customer-hourly"
                    type="number" step="0.01" min="0"
                    value={settings.end_customer_hourly_rate ?? ""}
                    onChange={(e) => setSettings({ ...settings, end_customer_hourly_rate: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Carnet 10 ore (€)</Label>
                  <Input
                    data-testid="settings-customer-carnet"
                    type="number" step="0.01" min="0"
                    value={settings.end_customer_carnet_10h_price ?? ""}
                    onChange={(e) => setSettings({ ...settings, end_customer_carnet_10h_price: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">📧 Email contabilità (report acquisti per fattura)</Label>
            <Input
              data-testid="settings-accounting-email"
              type="email"
              value={settings.accounting_email || ""}
              onChange={(e) => setSettings({ ...settings, accounting_email: e.target.value })}
              placeholder="contabilita@tuaazienda.it"
              className="mt-1 rounded-sm max-w-md"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              Ad ogni acquisto ore riuscito viene inviato in automatico un report con importo, IVA e dati cliente per l&apos;emissione fattura fiscale.
            </div>
          </div>
        </Card>

        <PriceHistoryCard refreshKey={historyKey} />

        <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-company">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-brand-muted text-brand flex items-center justify-center">
              <SettingsIcon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Dati azienda</h3>
              <p className="text-sm text-zinc-600">Compaiono nell&apos;intestazione dei preventivi PDF.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Ragione sociale</Label>
              <Input
                data-testid="settings-company-name"
                value={settings.company_name || ""}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">P.IVA</Label>
              <Input
                data-testid="settings-company-vat"
                value={settings.company_vat || ""}
                onChange={(e) => setSettings({ ...settings, company_vat: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Indirizzo</Label>
              <Input
                data-testid="settings-company-address"
                value={settings.company_address || ""}
                onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                className="mt-1 rounded-sm"
              />
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button
            data-testid="settings-save"
            onClick={save}
            disabled={saving}
            className="bg-brand hover:bg-brand-dark text-white rounded-sm"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : <><Save className="h-4 w-4 mr-2" /> Salva impostazioni</>}
          </Button>
        </div>

        <SmtpCard />
        <EmailLogCard />

        <Card className="rounded-sm border-zinc-200 bg-white p-6" data-testid="settings-whatsapp-template">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-sm bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <MessageCircle className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-zinc-900">Template WhatsApp — Avvio chat</h3>
              <p className="text-sm text-zinc-600">
                Meta impone che il primo messaggio verso un cliente fuori dalla finestra 24h sia un template pre-approvato.
                Inserisci qui il <b>nome esatto</b> e la <b>lingua</b> del template che vuoi usare quando premi &quot;Avvia chat WhatsApp&quot;.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Nome template</Label>
              <Input
                data-testid="settings-wa-template-name"
                value={settings.whatsapp_open_template || ""}
                onChange={(e) => setSettings({ ...settings, whatsapp_open_template: e.target.value })}
                placeholder="hello_world"
                className="mt-1 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Lingua (locale)</Label>
              <Input
                data-testid="settings-wa-template-lang"
                value={settings.whatsapp_open_template_lang || ""}
                onChange={(e) => setSettings({ ...settings, whatsapp_open_template_lang: e.target.value })}
                placeholder="en_US"
                className="mt-1 rounded-sm font-mono"
              />
            </div>
          </div>
          <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
            Trovi il nome nel <a className="text-brand hover:underline" href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noreferrer">Meta Business Manager → Template messaggi</a>.
            Il template deve essere in stato <b>Approvato</b>. Esempi comuni: <code>hello_world</code> (en_US), <code>default</code>.
            Se lasci vuoto viene usato il valore da variabile ambiente (fallback).
          </div>
        </Card>

        <WhatsAppTemplateStatsCard />

        <GenericWarrantyQrCard />

        <SoftwareLicensesCard />
      </div>
    </div>
  );
}
